/* eslint-disable */
import { createSlice } from '@reduxjs/toolkit'
import { adminApi } from '../apis/admin.api'
import {
  CalendarIcon,
  FolderIcon,
  DocumentChartBarIcon,
  UsersIcon
} from '@heroicons/react/24/outline'
import { DplResultsComponent } from '../../components/DeniedPersonList/DplResultsComponent'
import { OktaProfileComponent } from '../../components/OktaProfileComponent/OktaProfileComponent'
import { OktaProfileLogs } from '../../components/OktaProfileComponent/OktaProfileLogs'
import { OktaPIIComponent } from '../../components/PIIComponent/OktaPIIComponent'
import { DplClearComponent } from '../../components/DeniedPersonList/DplClearComponent'
import { SendActivationEmail } from '../../components/EmailValidation/SendActivationEmail'
import { SendActivationLink } from '../../components/EmailValidation/SendActivationLink'
import { PasswordResetEmail } from '../../components/PasswordReset/PasswordResetEmail'
import { TemporaryPassword } from '../../components/PasswordReset/TemporaryPasswords'
import { OktaProfileDelete } from '../../components/OktaProfileComponent/OktaProfileDelete'
import { AttributeManagement } from '../../components/AttributeManagement/AttributeManagement'
import { AppFunction } from '../../auth/roles'

const BTN_DEFAULT_CLASS_NAME = 'btn btn-primary'
const DEFAULT_CLASS_NAME =
  'mt-1 p-2 bg-white border shadow-sm border-slate-300 placeholder-slate-400 focus:outline-none focus:border-sky-500 focus:ring-sky-500 block w-full rounded-md sm:text-sm focus:ring-1'
const DEFAULT_LABEL_CLASS_NAME =
  "after:content-['*'] after:ml-0.5 after:text-red-500 block text-sm font-medium text-slate-700"

export interface AdminStateType {
  prefix: string
  count: number
  userType?: string
  message: string
  password: string
  ssosid: string
  allowedDomains: string[]
  isLoggedIn: boolean
  navigation: any[]
  pages: any[]
  userNavigation: any[]
}

export const initialState: AdminStateType = {
  prefix: 'support-admin',
  count: 0,
  userType: undefined,
  message: '',
  password: '',
  ssosid: '',
  allowedDomains: ['splunk.com'],
  isLoggedIn: false,
  navigation: [
    {
      name: 'profile',
      label: 'Profile',
      current: false,
      children: [
        {
          label: 'Okta Profile',
          name: 'okta-profile',
          icon: FolderIcon,
          current: false,
          contentComponent: OktaProfileComponent,
          formConfig: [],
          requiredFunction: AppFunction.OktaProfile
        },
        {
          label: 'Okta Profile Logs',
          name: 'logs',
          icon: DocumentChartBarIcon,
          current: false,
          contentComponent: OktaProfileLogs,
          formConfig: [],
          requiredFunction: AppFunction.OktaProfile
        },
        {
          label: 'Okta PII Data',
          name: 'pii',
          icon: DocumentChartBarIcon,
          current: false,
          contentComponent: OktaPIIComponent,
          formConfig: [],
          requiredFunction: AppFunction.OktaProfile
        },
        {
          label: 'Delete Okta Profile',
          name: 'delete-okta-profile',
          icon: FolderIcon,
          current: false,
          contentComponent: OktaProfileDelete,
          formConfig: [],
          requiredFunction: AppFunction.DeleteOktaProfile
        }
      ]
    },
    {
      name: 'denied-person-list',
      label: 'Denied Person List',
      current: false,
      children: [
        {
          label: 'Find Users In DPL Table',
          name: 'find-dpl-users',
          description: 'Type the user to search in DPL table',
          icon: FolderIcon,
          current: false,
          contentComponent: DplResultsComponent,
          requiredFunction: AppFunction.FindUserInDPL,
          formConfig: [
            {
              name: 'email',
              label: 'Email',
              componentType: 'text',
              defaultValue: 'example@email.com',
              labelClassName: DEFAULT_LABEL_CLASS_NAME,
              formClassName: DEFAULT_CLASS_NAME
            },
            {
              name: 'submit',
              label: 'Search',
              componentType: 'button',
              containerClassName: 'text-right p-2',
              formClassName: BTN_DEFAULT_CLASS_NAME
            }
          ]
        },
        {
          label: 'Clear Users from DPL Table',
          name: 'clear-user-dpl',
          icon: UsersIcon,
          description: 'Type the user to clear it from DPL table',
          current: false,
          contentComponent: DplClearComponent,
          requiredFunction: AppFunction.ClearUserFromDPL,
          formConfig: [
            {
              name: 'email',
              label: 'Email',
              componentType: 'text',
              defaultValue: 'example@email.com',
              labelClassName: DEFAULT_LABEL_CLASS_NAME,
              formClassName: DEFAULT_CLASS_NAME
            },
            {
              name: 'submit',
              label: 'Remove from DPL',
              componentType: 'button',
              containerClassName: 'text-right p-2',
              formClassName: BTN_DEFAULT_CLASS_NAME
            }
          ]
        }
      ]
    },
    {
      label: 'Email Delivery',
      name: 'email-delivery',
      current: false,
      children: [
        {
          label: 'Password Reset',
          name: `password-reset`,
          icon: FolderIcon,
          description: 'Trigger Password Reset',
          current: false,
          contentComponent: PasswordResetEmail,
          requiredFunction: AppFunction.PasswordReset,
          formConfig: [
            {
              name: 'email',
              label: 'Email',
              componentType: 'text',
              defaultValue: 'example@email.com',
              labelClassName: DEFAULT_LABEL_CLASS_NAME,
              formClassName: DEFAULT_CLASS_NAME
            }
          ]
        },
        {
          label: 'Set Temporary Password',
          name: `password-otp`,
          icon: FolderIcon,
          current: false,
          contentComponent: TemporaryPassword,
          requiredFunction: AppFunction.SetTemporaryPassword,
          formConfig: [
            {
              name: 'email',
              label: 'Email',
              componentType: 'text',
              defaultValue: 'example@email.com',
              labelClassName: DEFAULT_LABEL_CLASS_NAME,
              formClassName: DEFAULT_CLASS_NAME
            }
          ]
        },
        {
          label: 'Activation Email',
          name: 'activation-email',
          icon: CalendarIcon,
          description: 'Trigger Activation Email',
          current: false,
          contentComponent: SendActivationEmail,
          requiredFunction: AppFunction.ActivationEmail,
          formConfig: [
            {
              name: 'email',
              label: 'Email',
              componentType: 'text',
              defaultValue: 'example@email.com',
              labelClassName: DEFAULT_LABEL_CLASS_NAME,
              formClassName: DEFAULT_CLASS_NAME
            }
          ]
        },
        // {
        //   label: 'Validation Status',
        //   name: 'validation-status',
        //   icon: InboxIcon,
        //   description: 'Change email validation status flag',
        //   current: false,
        //   contentComponent: DplResultsComponent,
        //   formConfig: [
        //     {
        //       name: 'email',
        //       label: 'Email',
        //       componentType: 'text',
        //       defaultValue: 'example@email.com',
        //       labelClassName: DEFAULT_LABEL_CLASS_NAME,
        //       formClassName: DEFAULT_CLASS_NAME
        //     },
        //     {
        //       name: 'submit',
        //       label: 'Search',
        //       componentType: 'button',
        //       containerClassName: 'text-right p-2',
        //       formClassName: BTN_DEFAULT_CLASS_NAME
        //     }
        //   ]
        // },
        {
          label: 'Generate Link',
          name: 'generate-link',
          description: 'Generate activation/factor email link',
          icon: FolderIcon,
          current: false,
          contentComponent: SendActivationLink,
          requiredFunction: AppFunction.GenerateLink,
          formConfig: [
            {
              name: 'email',
              label: 'Email',
              componentType: 'text',
              defaultValue: 'example@email.com',
              labelClassName: DEFAULT_LABEL_CLASS_NAME,
              formClassName: DEFAULT_CLASS_NAME
            }
          ]
        }
      ]
    },
    {
      label: 'Attribute Management',
      name: 'attribute-management',
      current: false,
      children: [
          {
            label: 'Manage Attributes',
            name: `manage-attributes`,
            icon: FolderIcon,
            description: 'Define attributes for a specific application',
            current: false,
            contentComponent: AttributeManagement,
            requiredFunction: AppFunction.AttributeManagement,
            formConfig: []
          }
        ]
      }
    /*
        {
          label: 'Create User',
          name: 'create-user',
          icon: CalendarIcon,
          current: false,
          contentComponent: DplResultsComponent,
          formConfig: [
            {
              name: 'email',
              label: 'Email',
              componentType: 'text',
              defaultValue: 'example@email.com',
              labelClassName: DEFAULT_LABEL_CLASS_NAME,
              formClassName: DEFAULT_CLASS_NAME
            },
            {
              name: 'submit',
              label: 'Search',
              componentType: 'button',
              containerClassName: 'text-right p-2',
              formClassName: BTN_DEFAULT_CLASS_NAME
            }
          ]
        }
      ]
    }*/
  ],
  userNavigation: [
    { name: 'Your Profile', href: '#' },
    { name: 'Settings', href: '#' },
    { name: 'Sign out', href: '#' }
  ],
  pages: [{ name: 'Home', href: '/', current: true }]
}
export const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    increment: (state) => {
      state.count += 1
    },
    clear: (state) => {
      state.count = 0
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      adminApi.endpoints.getWhitelistedDomains.matchFulfilled,
      (state, { payload }) => {
        return {
          ...state,
          allowedDomains: [...state.allowedDomains, ...payload]
        }
      }
    )

    builder.addMatcher(
      adminApi.endpoints.isUserLoggedIn.matchFulfilled,
      (state, { payload }) => {
        return {
          ...state,
          isLoggedIn: payload
        }
      }
    )
  }
})

export const { increment, clear } = adminSlice.actions

export default adminSlice.reducer
